import { Badge, Box, Flex, HStack, Image, Text, VStack } from "@chakra-ui/react";
import { Instant, LocalDate } from "@js-joda/core";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../core/auth";
import { env } from "../../../env";
import MissingTaskBanner from "../../patient/components/MissingTaskBanner";
import PatientClinicalSeverityLevelTag from "../../patient/components/PatientClinicalSeverityLevelTag";
import PatientOutsourcingInfoBar from "../../patient/components/PatientOutsourcingInfoBar";
import { PatientClinicalSeverityLevel } from "../../patient/patient-utils";
import { queryKeys } from "../../../shared/query-keys";
import { durationFormatter } from "../../../shared/utils/duration-formatter";
import { getFullName } from "../../../shared/utils/get-full-name";
import EntityModalButton, {
  EntityModalButtonGroup,
} from "../../entityProfileModal/components/EntityModalButton";
import { entityProfileModalColors } from "../../entityProfileModal/entityProfileModal.theme";
import ProfileWindowSettingsButton from "../../profileWindowSettings/ProfileWindowSettingsButton";
import type { PatientProfileBridgeActions } from "../patientProfileModal.actions";
import { PatientProfileDisplayData } from "../patientProfileModal.mocks";
import { PatientProfilePatient } from "../patientProfileModal.types";
import { formatPatientProfileValue, getPatientPhotoUrl } from "../patientProfileModal.utils";

type Props = {
  actions: PatientProfileBridgeActions;
  displayData: PatientProfileDisplayData;
  patient: PatientProfilePatient;
  showBillingFeature: boolean;
  onOpenCalendar?: () => void;
};

function HeaderMetaField(props: { label: string; value: React.ReactNode }) {
  return (
    <Text>
      <Box as="span" fontWeight={700}>
        {props.label}:
      </Box>{" "}
      {props.value}
    </Text>
  );
}

type PatientHeaderOptionalFields = PatientProfilePatient & {
  appInstalled?: boolean;
  isOnVacationToday?: boolean;
  lastSeen?: Instant | number | null;
};

function isPatientOnVacationToday(patient: PatientProfilePatient) {
  if ((patient as PatientHeaderOptionalFields).isOnVacationToday === true) {
    return true;
  }

  const today = LocalDate.now().toString();

  return (patient.vacations ?? []).some((vacation) => String(vacation.date) === today);
}

function hasRnPlatformContract(patient: PatientProfilePatient) {
  return (patient.contracts ?? []).some(
    (contract) => (contract as { isRnPlatformClient?: boolean }).isRnPlatformClient === true
  );
}

function getLastSeenLabel(patient: PatientProfilePatient) {
  const { appInstalled, lastSeen } = patient as PatientHeaderOptionalFields;

  if (appInstalled !== true || lastSeen === null || lastSeen === undefined || lastSeen === -1) {
    return null;
  }

  if (lastSeen instanceof Instant) {
    return durationFormatter.relativeFrom(lastSeen).formatted;
  }

  if (typeof lastSeen === "number") {
    return durationFormatter.relativeFrom(Instant.ofEpochMilli(lastSeen)).formatted;
  }

  return null;
}

export default function PatientProfileHeader(props: Props) {
  const { actions, displayData, patient } = props;
  const queryClient = useQueryClient();
  const linkedPatient = patient.linkedPatient;
  const canCreateNewVisit = auth.isAllowedTo("allow_patient_page_new_visit");
  const authState = auth.getAuthState();
  const isOnboardingAgency =
    authState.type === "authenticated" && authState.data.agency.id === env.MEDFLYT_AGENCY_ID;
  const [isExportingProfile, setIsExportingProfile] = React.useState(false);
  const lastSeenLabel = getLastSeenLabel(patient);
  const isOnVacationToday = isPatientOnVacationToday(patient);
  const shouldShowOutsourcingBar = isOnboardingAgency && hasRnPlatformContract(patient);
  const invalidatePatient = React.useCallback(() => {
    const queryKey = queryKeys.patient.get(patient.id);

    queryClient.invalidateQueries({ queryKey });
    queryClient.refetchQueries({ queryKey, type: "active" });
  }, [patient.id, queryClient]);

  return (
    <Flex
      alignItems="flex-start"
      flexDirection={{ base: "column", xl: "row" }}
      gap={4}
      justifyContent="space-between"
      px={{ base: 4, md: 6, xl: 8 }}
      py={4}
    >
      <Flex gap={4} minW={0} w={{ base: "100%", xl: "auto" }}>
        <Image
          alt=""
          borderRadius="full"
          boxSize={{ base: "64px", md: "70px" }}
          flexShrink={0}
          objectFit="cover"
          src={getPatientPhotoUrl(patient)}
        />
        <VStack alignItems="flex-start" gap={1} minW={0}>
          <HStack alignItems="center" flexWrap="wrap" spacing={2}>
            <Text
              color={entityProfileModalColors.hoverTitle}
              fontSize={{ base: "22px", md: "25px" }}
              fontWeight={600}
              lineHeight="1.1"
            >
              {getFullName(patient)}
            </Text>
            {patient.displayId !== null && (
              <Text
                color={entityProfileModalColors.hoverTitle}
                fontSize={{ base: "20px", md: "22px" }}
                fontWeight={500}
                lineHeight="1.1"
              >
                ({patient.displayId})
              </Text>
            )}
            <Badge
              borderRadius="2px"
              colorScheme={patient.status === "ACTIVE" ? "green" : "orange"}
              fontSize="11px"
              px={2}
              py={1}
            >
              {patient.status}
            </Badge>
            {props.showBillingFeature && (
              <>
                {patient.eligibiltyStatus?.isEligible === false && (
                  <Badge borderRadius="2px" colorScheme="red" fontSize="11px" px={2} py={1}>
                    NOT ELIGIBLE
                  </Badge>
                )}
                {linkedPatient.linkedWith === null ? (
                  <EntityModalButton
                    iconClassName="fa fa-plus"
                    kind="blue"
                    size="xs"
                    onClick={() => actions.linkPatient({ patient })}
                  >
                    Link patient
                  </EntityModalButton>
                ) : (
                  <HStack fontSize="14px" spacing={1}>
                    <Text color={entityProfileModalColors.secondaryText}>
                      {linkedPatient.mutualLink ? "mutual with:" : "linked with:"}
                    </Text>
                    <EntityModalButton
                      kind="blue"
                      style={{
                        color: entityProfileModalColors.hoverTitle,
                        fontSize: "14px",
                        fontWeight: 700,
                        textDecoration: "underline",
                      }}
                      variant="link"
                      onClick={() => actions.navigateToPatient(linkedPatient.linkedWith!)}
                    >
                      {linkedPatient.linkedPatientName}
                    </EntityModalButton>
                    <EntityModalButton
                      aria-label="Remove linked patient"
                      iconClassName="fa fa-times"
                      kind="red"
                      size="xs"
                      title="Remove linked patient"
                      variant="outline"
                      onClick={() =>
                        actions.removeLinkedPatient({
                          patient,
                          onDone: invalidatePatient,
                        })
                      }
                    />
                  </HStack>
                )}
              </>
            )}
          </HStack>
          <Text
            color={entityProfileModalColors.muted}
            fontSize="14px"
            lineHeight="1.25"
            overflowWrap="anywhere"
          >
            {displayData.addressText}
          </Text>
          <HStack
            color={entityProfileModalColors.secondaryText}
            flexWrap="wrap"
            fontSize="14px"
            spacing={3}
          >
            <HeaderMetaField label="DOB" value={formatPatientProfileValue(patient.dateOfBirth)} />
            <HeaderMetaField label="Coordinator" value={displayData.assignedCoordinatorName} />
            <HeaderMetaField label="Office" value={displayData.currentOfficeName} />
          </HStack>
          <VStack alignItems="flex-start" gap={2} pt={1}>
            {patient.clinicalSeverityLevel !== null && (
              <PatientClinicalSeverityLevelTag
                clinicalSeverityLevel={
                  patient.clinicalSeverityLevel as PatientClinicalSeverityLevel
                }
                size="sm"
              />
            )}
            {shouldShowOutsourcingBar && <PatientOutsourcingInfoBar />}
            {isOnboardingAgency && <MissingTaskBanner patient={patient as never} />}
            {lastSeenLabel !== null && (
              <Text color={entityProfileModalColors.secondaryText} fontSize="15px">
                Last seen: {lastSeenLabel}
              </Text>
            )}
          </VStack>
        </VStack>
        {isOnVacationToday && (
          <HStack
            alignSelf="center"
            bg="pink.50"
            border="1px solid"
            borderColor="pink.200"
            borderRadius="4px"
            color="pink.700"
            fontSize="14px"
            fontWeight={700}
            px={3}
            py={2}
          >
            <Image alt="" boxSize="16px" src="admin/images/icons/tree.svg" />
            <Text>On Vacation</Text>
          </HStack>
        )}
      </Flex>

      <HStack
        flexShrink={0}
        flexWrap="wrap"
        justifyContent={{ base: "flex-start", xl: "flex-end" }}
        spacing={2}
        w={{ base: "100%", xl: "auto" }}
      >
        <ProfileWindowSettingsButton />
        <EntityModalButtonGroup>
          <EntityModalButton
            iconClassName="fa fa-paperclip"
            isLoading={isExportingProfile}
            kind="blue"
            size="sm"
            onClick={() => {
              setIsExportingProfile(true);
              actions.exportProfile({
                patient,
                onDone: () => setIsExportingProfile(false),
              });
            }}
          >
            Export Profile
          </EntityModalButton>
          <EntityModalButton
            aria-label="Export profile settings"
            disabled={isExportingProfile}
            iconClassName="fa fa-cog"
            kind="blue"
            size="sm"
            title="Export profile settings"
            variant="outline"
            onClick={() => actions.openExportProfileSettings({ patient })}
          />
        </EntityModalButtonGroup>
        <EntityModalButton
          iconClassName="fa fa-comments-o"
          kind="blue"
          size="sm"
          onClick={() => actions.openChat({ patient })}
        >
          {isOnboardingAgency ? "Communications" : "Chat"}
        </EntityModalButton>
        {isOnboardingAgency && patient.status === "DRAFT" && (
          <EntityModalButton
            iconClassName="fa fa-external-link"
            kind="blue"
            size="sm"
            onClick={() => actions.openIntakeModal({ patient })}
          >
            Open Intake Modal
          </EntityModalButton>
        )}
        <EntityModalButton
          iconClassName="fa fa-pencil"
          kind="blue"
          size="sm"
          onClick={() => actions.openNotes({ patient })}
        >
          Notes
        </EntityModalButton>
        {canCreateNewVisit && (
          <EntityModalButton
            iconClassName="fa fa-calendar"
            kind="blue"
            size="sm"
            onClick={() => {
              props.onOpenCalendar?.();
              actions.openNewVisit({ patient });
            }}
          >
            New Visit
          </EntityModalButton>
        )}
        <EntityModalButton
          iconClassName="fa fa-plus"
          kind="blue"
          size="sm"
          onClick={() => actions.openNewTask({ patient })}
        >
          New Task
        </EntityModalButton>
      </HStack>
    </Flex>
  );
}
